import styled from 'styled-components';
import { GenericDataType } from 'modules/domains';
import { Mapping } from 'api/MappingsAPI';
import { pxToRem } from 'utils/convert';
import { InputText } from 'ui/inputs/inputText/InputText';
import { Search } from 'ui/icons/action/Search';
import {
  BasicSelectWithNestedChildren,
  SelectWrapper,
  BaseOption,
  SelectValueType,
} from 'ui/selects';

import { isDefined } from 'utils/tsHelpers';
import { useIsEventOption } from './hooks/useIsEventOption';
import { Trigger } from '../conditionValue';
import { useSearchFieldContext } from '../context/SearchFieldContext';
import { useTranslation } from '../../../hooks/useTranslation';
import { useConditionFieldNameController, useConditionFieldNameErrors } from '../../../hooks/form';
import { useResetConditionComparatorAndValue } from '../../../hooks/useResetConditionComparatorAndValue';
import { FieldSearchOption } from '../../../types';
import { useFieldsContext } from '../../../contexts';

const FIELD_SELECT_WIDTH = pxToRem(350);

const StyledBasicSelect = styled(BasicSelectWithNestedChildren)`
  ${({ theme }) => theme.styles.body.body2};
  width: fit-content;
  ${SelectWrapper} {
    border: none;
    background: none;
    :hover {
      border-color: none;
    }
  }
`;

export const convertFieldsToOptions = (fields: Mapping[]): BaseOption<string>[] =>
  fields.map(({ fieldName, fieldType }) => ({
    id: `${fieldName}-${fieldType}`,
    value: fieldName,
    label: fieldName,
    Suffix: fieldType,
  }));

export const DATA_TEST = {
  select: 'contact-property-select',
};

export const createGetSelection = (contactProperties: Mapping[]) => (values: SelectValueType[]) => {
  const options = contactProperties.filter(({ fieldName }) => fieldName === values[0]);
  const selectedOption = options[0];
  return selectedOption;
};

export const ContactPropertySelect = () => {
  const { t } = useTranslation();
  const {
    field: { onChange, value: fieldName },
  } = useConditionFieldNameController();
  const { changeDataType, changeOption } = useSearchFieldContext();
  const { aggregateFieldsByDataType, isLoading } = useFieldsContext();
  const isEventOption = useIsEventOption();
  const errors = useConditionFieldNameErrors();

  const userFields = aggregateFieldsByDataType(GenericDataType.User);
  const getSelection = createGetSelection(userFields);
  const resetConditionComparatorAndValue = useResetConditionComparatorAndValue();
  const userProfileFieldMappings = convertFieldsToOptions(userFields);
  const placeholder = t('selector.option.user');
  const setFieldName = (value: string) => onChange({ target: { value } });

  if (isEventOption) return null;

  return (
    <StyledBasicSelect
      data-test={DATA_TEST.select}
      isError={!!errors}
      isLoading={isLoading}
      options={userProfileFieldMappings}
      placeholder={placeholder}
      Trigger={Trigger}
      defaultValues={[fieldName]}
      width={FIELD_SELECT_WIDTH}
      onSelect={(options) => {
        const selection = getSelection(options);
        if (isDefined(selection)) {
          setFieldName(selection.fieldName);
          resetConditionComparatorAndValue(selection.fieldType);
          changeOption(FieldSearchOption.UserProfile);
          changeDataType(GenericDataType.User);
        }
      }}
    >
      <InputText
        type="search"
        autoComplete="off"
        id="user-profile-search"
        Prefix={<Search />}
        placeholder={placeholder}
        value=""
        autoFocus
      />
    </StyledBasicSelect>
  );
};
